import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { kycRefusalKind, type kycRequired } from '../../api/errors';
import { Alert } from '../../ui';

/**
 * R-05 — an order was refused because the account's KYC is not approved (CR-57 / D105).
 *
 * KYC gates the ORDER, not the account: the retailer got here by browsing and filling a cart, which
 * all worked. So this is not "that did not work". It says which of three things is true, because each
 * has a different next step, and it links to R-18 where the next step happens — a retailer who has
 * filled a cart and is refused with no explanation rings their account manager, which is the outcome
 * this whole change exists to prevent.
 *
 *   needed     nothing has reached Miraki   → go and send your documents
 *   reviewing  Miraki has them              → wait; nothing for you to do
 *   rejected   Miraki refused them          → here is what was wrong, in Miraki's words
 *
 * The cart is not touched by the refusal, and it says so: nothing is lost by being refused.
 */
export function KycRefusal({ kyc }: { kyc: NonNullable<ReturnType<typeof kycRequired>> }) {
  const { t } = useTranslation();
  const kind = kycRefusalKind(kyc);
  return (
    <Alert tone={kind === 'reviewing' ? 'them' : 'stop'}>
      <b>{t(`shop.order.kyc.${kind}.title`)}</b>{' '}
      {kind === 'rejected' ? t('shop.order.kyc.rejected.lead') : t(`shop.order.kyc.${kind}.body`)}
      {kind === 'rejected' ? (
        // Miraki's own words, unabridged — a retailer who is not told which file was wrong sends the same one again.
        <div style={{ margin: 'var(--mk-space-2) 0', whiteSpace: 'pre-wrap' }}>
          {kyc.reason ?? t('shop.order.kyc.rejected.noReason')}
        </div>
      ) : null}
      <div>
        {t('shop.order.kyc.cartSaved')}{' '}
        <Link to="/shop/account">{t(`shop.order.kyc.${kind}.link`)}</Link>
      </div>
    </Alert>
  );
}
